
import { motion, useInView } from 'framer-motion'
import { useRef } from 'react'
import { Linkedin, Mail, Phone, MapPin, ArrowUpRight, Instagram, Facebook } from 'lucide-react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import logo from '../assets/al-konda-logo-optimized.webp'
import footerBg from '../assets/Footer/background.avif'

const Footer = () => {
    const { t, i18n } = useTranslation()
    const ref = useRef(null)
    const isInView = useInView(ref, { once: true, margin: "-100px" })
    const isRTL = i18n.language === 'ar' || i18n.language === 'ku'

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.15,
                delayChildren: 0.3
            }
        }
    }

    const itemVariants = {
        hidden: { opacity: 0, y: 40 },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                duration: 0.8,
                ease: [0.22, 1, 0.36, 1]
            }
        }
    }

    return (
        <footer ref={ref} className="relative z-10 overflow-hidden rounded-t-3xl">
            {/* Glassmorphism background */}
            <div className="absolute inset-0">
                <img
                    src={footerBg}
                    alt="Background"
                    className="w-full h-full object-cover"
                    loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/30" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
            </div>

            {/* Glassmorphism overlay for entire footer */}
            <div className="absolute inset-0 bg-white/[0.02] backdrop-blur-2xl border border-white/5" />

            <div className="relative z-10 w-full px-4 sm:px-6 lg:px-8 py-8 sm:py-12 lg:py-16">

                <motion.div
                    variants={containerVariants}
                    initial="hidden"
                    animate={isInView ? "visible" : "hidden"}
                    className="relative z-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 sm:gap-10 lg:gap-12"
                >
                    {/* Brand Section - Enhanced */}
                    <motion.div variants={itemVariants} className="sm:col-span-2 lg:col-span-1 text-center lg:text-left">
                        <motion.div
                            className="flex flex-col sm:flex-row lg:flex-col xl:flex-row items-center justify-center lg:justify-start mb-6"
                            whileHover={{ scale: 1.02 }}
                            transition={{ duration: 0.4 }}
                        >
                            <div className={`relative mb-4 sm:mb-0 lg:mb-4 lg:mr-0 xl:mb-0 ${isRTL ? 'sm:ml-5 xl:ml-5' : 'sm:mr-5 xl:mr-5'}`}>
                                {/* ⭐️ UPDATED LOGO WRAPPER STYLES FOR CONSISTENCY */}
                                <motion.div
                                    className="w-16 h-16 sm:w-20 sm:h-20 bg-white rounded-2xl flex items-center justify-center group overflow-hidden shadow-lg shadow-black/20 p-1.5 border-none"
                                    whileHover={{
                                        scale: 1.05,
                                        backgroundColor: '#FFFFFF',
                                        boxShadow: '0 10px 25px rgba(0,0,0,0.3)'
                                    }}
                                    transition={{ duration: 0.4 }}
                                >
                                    <img
                                        src={logo}
                                        alt="Al-Konda Logo"
                                        className="w-full h-full object-contain transition-all duration-500"
                                        loading="lazy"
                                    />
                                </motion.div>
                            </div>
                            <div>
                                <span className="text-xl sm:text-2xl font-light text-white tracking-widest block drop-shadow-lg">AL-KONDA</span>
                                <motion.div
                                    className="h-px w-12 bg-gradient-to-r from-white/30 to-white/10 mt-2 mx-auto lg:mx-0"
                                    whileHover={{ width: 60 }}
                                    transition={{ duration: 0.4 }}
                                />
                            </div>
                        </motion.div>

                        <p className="text-white/80 mb-6 leading-relaxed font-light text-base sm:text-lg tracking-wide drop-shadow-md max-w-md mx-auto lg:mx-0">
                            {t('footer.description')}
                        </p>

                        <motion.div className={`flex justify-center lg:justify-start ${isRTL ? 'space-x-reverse space-x-3' : 'space-x-3'}`}>
                            {[
                                { Icon: Linkedin, color: 'hover:bg-blue-600/20 hover:border-blue-500/30', href: 'https://www.linkedin.com/company/al-konda-global/', label: 'LinkedIn' },
                                { Icon: Instagram, color: 'hover:bg-pink-600/20 hover:border-pink-500/30', href: 'https://www.instagram.com/alkondausa', label: 'Instagram' },
                                { Icon: Facebook, color: 'hover:bg-blue-500/20 hover:border-blue-400/30', href: 'https://www.facebook.com/thaeirslewakonda', label: 'Facebook' }
                            ].map(({ Icon, color, href, label }, index) => (
                                <motion.a
                                    key={index}
                                    href={href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    aria-label={`Visit our ${label} page`}
                                    className={`w-12 h-12 bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl flex items-center justify-center group transition-all duration-500 shadow-[0_8px_32px_rgba(0,0,0,0.2)] ${color}`}
                                    whileHover={{
                                        scale: 1.1,
                                        y: -3,
                                        boxShadow: '0 10px 25px rgba(0,0,0,0.2)'
                                    }}
                                    whileTap={{ scale: 0.95 }}
                                >
                                    <Icon className="w-5 h-5 text-white/70 group-hover:text-white transition-colors duration-500" />
                                </motion.a>
                            ))}
                        </motion.div>
                    </motion.div>

                    {/* Services - Enhanced */}
                    <motion.div variants={itemVariants} className="text-center sm:text-left">
                        <h4 className="text-lg sm:text-xl font-normal text-white mb-4 sm:mb-6 tracking-wider relative inline-block drop-shadow-lg">
                            {t('footer.services')}
                            <motion.div
                                className="absolute -bottom-2 left-1/2 sm:left-0 transform -translate-x-1/2 sm:translate-x-0 h-0.5 bg-gradient-to-r from-white/30 to-transparent w-1/2"
                                whileHover={{ width: '100%' }}
                                transition={{ duration: 0.4 }}
                            />
                        </h4>
                        <ul className="space-y-3 sm:space-y-2">
                            {[
                                { name: t('header.servicesList.construction'), href: '/services/construction' },
                                { name: t('header.servicesList.powerGeneration'), href: '/services/power-generation' },
                                { name: t('header.servicesList.environmental'), href: '/services/environmental' },
                                { name: t('header.servicesList.oilFuelSupply'), href: '/services/oil-fuel-supply' },
                                { name: t('header.servicesList.logistics'), href: '/services/logistics' },
                                { name: t('header.servicesList.generalTrading'), href: '/services/general-trading' },
                                { name: t('header.servicesList.recycling'), href: '/services/recycling' }
                            ].map((item, idx) => (
                                <motion.li
                                    key={item.name}
                                    initial={{ opacity: 0, x: -20 }}
                                    animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -20 }}
                                    transition={{ delay: 0.6 + idx * 0.1, duration: 0.6 }}
                                >
                                    <Link
                                        to={item.href}
                                        className="text-white/80 hover:text-white transition-all duration-500 flex items-center group font-light tracking-wide py-2 drop-shadow-sm hover:translate-x-2"
                                    >
                                        <div className="w-1.5 h-1.5 bg-white rounded-full mr-3 opacity-0 group-hover:opacity-100 transition-all duration-300 group-hover:scale-150" />
                                        <span className="flex-1">{item.name}</span>
                                        <ArrowUpRight
                                            className={`w-4 h-4 opacity-0 group-hover:opacity-100 transform group-hover:-translate-y-1 transition-all duration-500 ${isRTL ? 'mr-2 group-hover:-translate-x-1' : 'ml-2 group-hover:translate-x-1'}`}
                                        />
                                    </Link>
                                </motion.li>
                            ))}
                        </ul>
                    </motion.div>

                    {/* Company - Enhanced */}
                    <motion.div variants={itemVariants} className="text-center sm:text-left">
                        <h4 className="text-lg sm:text-xl font-normal text-white mb-4 sm:mb-6 tracking-wider relative inline-block drop-shadow-lg">
                            {t('footer.company')}
                            <motion.div
                                className="absolute -bottom-2 left-1/2 sm:left-0 transform -translate-x-1/2 sm:translate-x-0 h-0.5 bg-gradient-to-r from-white/30 to-transparent w-1/2"
                                whileHover={{ width: '100%' }}
                                transition={{ duration: 0.4 }}
                            />
                        </h4>
                        <ul className="space-y-3 sm:space-y-2">
                            {[
                                { name: t('footer.companyLinks.services'), href: '/services' },
                                { name: t('footer.companyLinks.contact'), href: '/contact' },
                                { name: t('footer.companyLinks.about'), href: '/about' },
                                { name: t('footer.companyLinks.privacy'), href: '/about#privacy-policy' }
                            ].map((item, idx) => (
                                <motion.li
                                    key={item.name}
                                    initial={{ opacity: 0, x: -20 }}
                                    animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -20 }}
                                    transition={{ delay: 0.8 + idx * 0.1, duration: 0.6 }}
                                >
                                    <Link
                                        to={item.href}
                                        className="text-white/80 hover:text-white transition-all duration-500 flex items-center group font-light tracking-wide py-2 drop-shadow-sm hover:translate-x-2"
                                    >
                                        <div className="w-1.5 h-1.5 bg-white rounded-full mr-3 opacity-0 group-hover:opacity-100 transition-all duration-300 group-hover:scale-150" />
                                        <span className="flex-1">{item.name}</span>
                                        <ArrowUpRight
                                            className={`w-4 h-4 opacity-0 group-hover:opacity-100 transform group-hover:-translate-y-1 transition-all duration-500 ${isRTL ? 'mr-2 group-hover:-translate-x-1' : 'ml-2 group-hover:translate-x-1'}`}
                                        />
                                    </Link>
                                </motion.li>
                            ))}
                        </ul>
                    </motion.div>

                    {/* Contact - Enhanced */}
                    <motion.div variants={itemVariants} className="sm:col-span-2 lg:col-span-1 text-center sm:text-left">
                        <h4 className="text-lg sm:text-xl font-normal text-white mb-4 sm:mb-6 tracking-wider relative inline-block drop-shadow-lg">
                            {t('footer.contactInfo')}
                            <motion.div
                                className="absolute -bottom-2 left-1/2 sm:left-0 transform -translate-x-1/2 sm:translate-x-0 h-0.5 bg-gradient-to-r from-white/30 to-transparent w-1/2"
                                whileHover={{ width: '100%' }}
                                transition={{ duration: 0.4 }}
                            />
                        </h4>
                        <div className="space-y-4 sm:space-y-6">
                            {[
                                {
                                    icon: Phone,
                                    text: t('footer.phone'),
                                    href: 'tel:+17608078805'
                                },
                                {
                                    icon: Mail,
                                    text: 'coo@al-konda.com\nco@al-konda.com',
                                    href: 'mailto:coo@al-konda.com'
                                },
                                {
                                    icon: MapPin,
                                    text: 'Ankawa Two Side Street\nErbil, Iraq',
                                    href: 'https://www.google.com/maps/place/Al-konda+company/@36.233341,43.997724,17z/data=!3m1!4b1!4m6!3m5!1s0x40071f5a44582b0d:0x6fd7b2c2211869a1!8m2!3d36.233341!4d43.997724!16s%2Fg%2F11j8yzl6g5?entry=ttu'
                                }
                            ].map((contact, idx) => (
                                <motion.div
                                    key={idx}
                                    className={`flex items-start justify-center sm:justify-start group p-3 rounded-lg transition-all duration-500 hover:bg-white/5 ${isRTL ? 'flex-row-reverse' : ''}`}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                                    transition={{ delay: 1 + idx * 0.1, duration: 0.6 }}
                                    whileHover={{ y: -2 }}
                                >
                                    <div className={`w-10 h-10 bg-white/5 backdrop-blur-sm border border-white/10 rounded-lg flex items-center justify-center mt-0.5 group-hover:bg-white/10 transition-all duration-500 shadow-[0_8px_32px_rgba(0,0,0,0.2)] flex-shrink-0 ${isRTL ? 'mr-4' : 'mr-4'}`}>
                                        <contact.icon className="w-4 h-4 text-white/70 group-hover:text-white transition-colors duration-500" />
                                    </div>
                                    <span className={`text-white/80 font-light whitespace-pre-line leading-relaxed tracking-wide group-hover:text-white/90 transition-colors duration-500 drop-shadow-sm text-sm sm:text-base ${idx === 0 && isRTL ? 'dir-ltr' : ''}`}>
                                        {contact.text}
                                    </span>
                                </motion.div>
                            ))}
                        </div>
                    </motion.div>
                </motion.div>

                {/* Enhanced Bottom Section */}
                <motion.div
                    initial={{ opacity: 0, y: 40 }}
                    animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
                    transition={{ delay: 1.4, duration: 0.8 }}
                    className="relative mt-8 sm:mt-12 lg:mt-16 pt-6 sm:pt-8"
                >
                    {/* Premium divider */}
                    <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent">
                        <div className="absolute top-0 left-1/2 w-32 h-px bg-gradient-to-r from-white/0 via-white/30 to-white/0 transform -translate-x-1/2" />
                    </div>

                    <div className="flex justify-center items-center">
                        <motion.p
                            className="text-white/60 text-xs sm:text-sm font-light tracking-wide drop-shadow-sm text-center"
                            whileHover={{ color: 'rgba(255,255,255,0.8)' }}
                            transition={{ duration: 0.3 }}
                        >
                            © {new Date().getFullYear()} Al-Konda. All rights reserved.
                        </motion.p>
                    </div>
                </motion.div>
            </div>
        </footer>
    )
}

export default Footer